import React, { useEffect, useRef, useState } from "react";
import { X, Check, Loader2, ChevronDown } from "lucide-react";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
  CommandGroup,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

/**
 * AsyncMultiSelect — searchable multi-select with server-side fetch.
 *
 * Props:
 *  - value:         array of selected ids
 *  - valueLabels:   { [id]: label } cache used to render selected chips
 *  - onChange:      (ids[], { [id]: label }) => void
 *  - fetchOptions:  async (search:string) => [{id, label, hint?}]
 *  - placeholder:   string
 *  - emptyLabel:    string
 *  - testId:        base data-testid
 *  - disabled
 */
export default function AsyncMultiSelect({
  value = [],
  valueLabels = {},
  onChange,
  fetchOptions,
  placeholder = "Search & add…",
  emptyLabel = "Type to search…",
  testId = "async-multi-select",
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [labelCache, setLabelCache] = useState(valueLabels || {});
  const debounceRef = useRef();

  // Merge any incoming valueLabels updates into the cache
  useEffect(() => {
    if (!valueLabels) return;
    setLabelCache((c) => ({ ...valueLabels, ...c }));
  }, [valueLabels]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const list = await fetchOptions(q);
        setOptions(list || []);
        setLabelCache((c) => {
          const next = { ...c };
          for (const o of (list || [])) next[o.id] = o.label;
          return next;
        });
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [q, open, fetchOptions]);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const toggle = (id, label) => {
    const cur = new Set(value);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    const next = Array.from(cur);
    const nextCache = { ...labelCache };
    if (label) nextCache[id] = label;
    setLabelCache(nextCache);
    onChange(next, nextCache);
  };

  const remove = (id) => {
    const next = value.filter((v) => v !== id);
    onChange(next, labelCache);
  };

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-gray-100 border border-gray-200 text-xs"
              data-testid={`${testId}-chip-${id}`}
            >
              <span className="text-gray-900 font-medium max-w-[180px] truncate">
                {labelCache[id] || "Loading…"}
              </span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(id)}
                  className="h-4 w-4 rounded hover:bg-gray-200 flex items-center justify-center text-gray-500"
                  title="Remove"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className="w-full h-10 justify-between font-normal px-3 hover:bg-white"
            data-testid={`${testId}-trigger`}
          >
            <span className="text-gray-500 text-sm truncate">
              {value.length === 0 ? placeholder : `Add another (${value.length} selected)`}
            </span>
            <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0 shadow-lg border-gray-200"
          align="start"
          sideOffset={4}
        >
          <Command shouldFilter={false} className="bg-white">
            <CommandInput
              value={q}
              onValueChange={setQ}
              placeholder="Type to search…"
              className="h-11 text-sm"
              data-testid={`${testId}-search`}
            />
            <CommandList className="h-[280px] max-h-[280px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  <span className="text-xs">Searching…</span>
                </div>
              ) : (
                <>
                  <CommandEmpty className="text-center text-xs text-gray-400 py-8">
                    {q ? `No results for "${q}"` : emptyLabel}
                  </CommandEmpty>
                  {options.length > 0 && (
                    <CommandGroup className="[&_[cmdk-group-heading]]:hidden">
                      {options.map((o) => {
                        const selected = value.includes(o.id);
                        return (
                          <CommandItem
                            key={o.id}
                            value={o.id}
                            onSelect={() => toggle(o.id, o.label)}
                            className={`cursor-pointer px-3 py-2 flex items-start gap-2 ${
                              selected ? "bg-amber-50/40" : ""
                            }`}
                            data-testid={`${testId}-option-${o.id}`}
                          >
                            {selected ? (
                              <Check className="h-4 w-4 text-[#9B1C1C] flex-shrink-0 mt-0.5" />
                            ) : (
                              <span className="w-4 flex-shrink-0" />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-gray-900 truncate leading-tight">{o.label}</p>
                              {o.hint && (
                                <p className="text-[11px] text-gray-500 font-mono truncate">{o.hint}</p>
                              )}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
